import type { DataProvider } from '@refinedev/core';
import Config from 'src/common/Config';
import { fetchWrapper } from 'src/common/utils/api';

const API_URL = Config.getApiUrl();

export const dataProvider: DataProvider = {
  getOne: async ({ resource, id }) => {
    const idPath = id ? `/${id}` : '';
    const url = `${API_URL}/${resource}${idPath}`;
    return await fetchWrapper(
      url,
      {
        credentials: 'include',
      },
      'getOne',
    );
  },
  update: async ({ resource, id, variables }) => {
    const idPath = id ? `/${id}` : '';
    const url = `${API_URL}/${resource}${idPath}`;

    return await fetchWrapper(
      url,
      {
        method: 'PUT',
        body: JSON.stringify(variables),
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      },
      'update',
    );
  },
  getList: async ({ resource, filters, pagination }) => {
    let url = `${API_URL}/${resource}`;
    const queryParams = new URLSearchParams();

    if (filters) {
      filters.forEach(filter => {
        if ('field' in filter) {
          if (filter.operator === 'eq') {
            queryParams.append(filter.field, filter.value);
          } else {
            queryParams.append(`${filter.field}_${filter.operator}`, filter.value);
          }
        }
      });
    }

    if (pagination && pagination.mode === 'server') {
      if (pagination.current) {
        queryParams.append('page', pagination.current.toString());
      }
      if (pagination.pageSize) {
        queryParams.append('size', pagination.pageSize.toString());
      }
    }

    url = `${url}?${queryParams.toString()}`;
    const result = await fetchWrapper(
      url,
      {
        credentials: 'include',
      },
      'getList',
    );

    if (result.data && Array.isArray(result.data.items) && typeof result.data.total === 'number') {
      return {
        data: result.data.items,
        total: result.data.total,
      };
    }

    return {
      ...result,
      total: result?.data?.total ?? 0,
    };
  },
  create: async ({ resource, variables, meta }) => {
    const query = meta?.query ? `?${new URLSearchParams(meta.query).toString()}` : '';
    return await fetchWrapper(
      `${API_URL}/${resource}${query}`,
      {
        method: 'POST',
        body: JSON.stringify(variables ?? {}),
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      },
      'create',
    );
  },
  deleteOne: async ({ resource, id }) => {
    return await fetchWrapper(
      `${API_URL}/${resource}/${id}`,
      {
        method: 'DELETE',
        credentials: 'include',
      },
      'deleteOne',
    );
  },
  deleteMany: async ({ resource, ids }) => {
    return await fetchWrapper(
      `${API_URL}/${resource}/delete-request`,
      {
        method: 'POST',
        body: JSON.stringify(ids),
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      },
      'deleteMany',
    );
  },
  custom: async ({ url, method, headers, payload, meta }) => {
    return await fetchWrapper(
      `${API_URL}/${url}`,
      {
        method,
        body: payload ? JSON.stringify(payload) : undefined,
        headers: {
          'Content-Type': 'application/json',
          ...(headers || {}),
        },
        credentials: 'include',
      },
      `custom ${method.toUpperCase()}`,
      { timeoutMs: (meta?.timeoutMs as number) ?? 25000 },
    );
  },

  getApiUrl: () => API_URL,
};
